import React from 'react';
import { array, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { EditListingPhotosForm } from '../../forms';
import { ensureOwnListing } from '../../util/data';
import css from './EditListingPhotosPanel.css';
import { ASSET_ENTITY_TYPE_LISTING, DEFAULT_LISTING_TITLE } from '../../marketplace-custom-config';
import { DEFAULT_VARIANT, SQUARE_SMALL_2X } from '../../ducks/Assets.duck';
import { useAssetsToSdk } from '../../hooks/useAssetsToSdk';
import { useSelector } from 'react-redux';
import { checkIfListingIsAnonymous } from '../../util/anonym';
import config from '../../config';
import { isDraft } from '../EditListingWizard/Helpers';

const { anonymousHorseName } = config;

const EditListingPhotosPanel = ({
    className,
    rootClassName,
    errors,
    fetchInProgress,
    newListingPublished,
    listing,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    onChange,
    onSubmit,
    createListingDraftInProgress,
    externalListing,
    params,
    ...rest
}) => {
    const rootClass = rootClassName || css.root;
    const classes = classNames(rootClass, className);
    const currentListing = ensureOwnListing(listing);
    const listingId = currentListing?.id?.uuid;
    const listingPublicData = currentListing?.attributes?.publicData || {};
    const title = currentListing?.attributes?.title;
    const description = currentListing?.attributes?.description;
    const {
        mainListingImage,
        anonymousListingPackageIntention,
        gender,
        age,
        hight,
        breed,
        color,
    } = listingPublicData;
    /** Redux store data */
    const listingAssets = useSelector(s => {
        const assets = s.Assets.assetsData;
        return (
            assets &&
            assets[listingId] &&
            (assets[listingId][DEFAULT_VARIANT] || assets[listingId][SQUARE_SMALL_2X])
        );
    });
    const uploadAssetsInProgress = useSelector(s => {
        const requests = s.Assets.assetsLoadingRequests;
        return requests && requests[listingId];
    });

    const {
        publicData: { anonymousListingTitle },
    } = currentListing.attributes;

    const isAnonymousListing = checkIfListingIsAnonymous(currentListing);

    const notEmptyListingTitle = isAnonymousListing
        ? anonymousListingTitle
        : title == DEFAULT_LISTING_TITLE
        ? ''
        : title;

    const images = useAssetsToSdk({
        assets: listingAssets,
        inProgress: uploadAssetsInProgress,
    });

    const isDraftURI = isDraft(params);

    return (
        <div className={classes}>
            <EditListingPhotosForm
                className={css.form}
                disabled={fetchInProgress}
                ready={newListingPublished}
                fetchErrors={errors}
                entityType={ASSET_ENTITY_TYPE_LISTING}
                initialValues={{
                    images,
                    otherFields: {
                        title: notEmptyListingTitle,
                        anonymousListingPackageIntention:
                            anonymousListingPackageIntention ||
                            (isDraftURI && isAnonymousListing) ||
                            null,
                        gender,
                        age,
                        hight,
                        description,
                        breed,
                        color,
                        mainListingImage,
                    },
                }}
                images={images}
                assets={listingAssets}
                uploadAssetsInProgress={uploadAssetsInProgress}
                onSubmit={values => {
                    const {
                        addImage,
                        title,
                        anonymousListingPackageIntention,
                        gender,
                        age,
                        hight,
                        description,
                        breed,
                        color,
                        isAutoSave,
                        redirectToListingPage,
                        hasVideo = false,
                        hasGalleryAssets = false,
                        mainListingImage,
                        ...updateValues
                    } = values;

                    const allValues = {
                        publicData: {
                            anonymousListingPackageIntention,
                            gender,
                            age,
                            hight,
                            breed,
                            color,
                            hasVideo,
                            hasGalleryAssets,
                            mainListingImage,
                        },
                        title: isAnonymousListing
                            ? anonymousHorseName
                            : title
                            ? title
                            : DEFAULT_LISTING_TITLE,
                        description,
                        ...updateValues,
                    };

                    if (isAutoSave) {
                        allValues.isAutoSave = isAutoSave;
                    }

                    if (redirectToListingPage) {
                        allValues.redirectToListingPage = redirectToListingPage;
                    }

                    onSubmit(allValues);
                }}
                onChange={onChange}
                saveActionMsg={submitButtonText}
                updated={panelUpdated}
                updateInProgress={updateInProgress}
                createListingDraftInProgress={createListingDraftInProgress}
                externalListing={externalListing}
                isAnonymousListing={isAnonymousListing}
                currentListing={currentListing}
                {...rest}
            />
        </div>
    );
};

EditListingPhotosPanel.propTypes = {
    className: string,
    rootClassName: string,
    errors: object,
    images: array,

    // We cannot use propTypes.listing since the listing might be a draft.
    listing: object,
    onSubmit: func.isRequired,
    onChange: func.isRequired,
    submitButtonText: string.isRequired,
    panelUpdated: bool.isRequired,
    updateInProgress: bool.isRequired,
    externalListing: bool,
};

export default EditListingPhotosPanel;
